import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstanceNew } from 'src/utils/axios';

function getApiErrorMessage(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
}

function normalizeWatcher(watcher = {}) {
  return {
    running: Boolean(watcher?.running), // poller on/off
    activeFetch: Boolean(watcher?.activeFetch ?? false), // fetch in progress
    lastSeenAt: watcher?.lastSeen ?? null,
    lastRunAt: watcher?.lastRunAt ?? null,
    lastSuccessAt: watcher?.lastSuccessAt ?? null,
    stopReason: watcher?.stopReason ?? null,
    pausedAt: watcher?.pausedAt ?? null,
    stoppedAt: watcher?.stoppedAt ?? null,
    lastError: watcher?.lastError ?? null,
    intervalMs: watcher?.intervalMs ?? null,
    state: watcher?.state ?? null,
  };
}

export const fetchProfitPercent = createAsyncThunk(
  'settings/fetchProfitPercent',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.get('/settings/profit-percent');
      return res.data;
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to fetch profit percent'));
    }
  }
);

export const bootstrapBankState = createAsyncThunk(
  'settings/bootstrapBankState',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.get('/admin/settings/bank-status');
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || 'Failed to bootstrap bank state'
      );
    }
  }
);

export const loginGolis = createAsyncThunk(
  'settings/loginGolis',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post('/admin/settings/login-golis');

      if (res?.status !== 200) {
        throw new Error(res?.data?.message || 'Failed to login');
      }

      await dispatch(bootstrapBankState()).unwrap();

      return {
        message: res?.data?.message || 'Login completed successfully.',
      };
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to login'));
    }
  }
);

export const logoutGolis = createAsyncThunk(
  'settings/logoutGolis',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post('/admin/settings/logout-golis');

      await dispatch(bootstrapBankState()).unwrap();

      return {
        message: res?.data?.message || 'Logged out from Golis.',
      };
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to logout from Golis'));
    }
  }
);

export const fetchGolisBalanceOnce = createAsyncThunk(
  'settings/fetchGolisBalanceOnce',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const resp = await axiosInstanceNew.post('/admin/settings/fetch-balance-golis');

      const payload = resp?.data || {};
      if (payload?.ok === false) {
        throw new Error(payload?.message || 'Failed to fetch balance');
      }

      await dispatch(bootstrapBankState()).unwrap();

      return {
        balance: payload,
        message: payload?.message || 'Balance fetched successfully.',
      };
    } catch (err) {
      const errorCode = err?.response?.data?.code || err?.response?.data?.error;

      if (errorCode === 'MERCHANT_SESSION_EXPIRED' || err?.message === 'MERCHANT_SESSION_EXPIRED') {
        try {
          await dispatch(bootstrapBankState()).unwrap();
        } catch (e) {
          // ignore refresh error
        }

        return rejectWithValue('Your Golis session expired. Please login again.');
      }

      return rejectWithValue(getApiErrorMessage(err, 'Failed to fetch balance'));
    }
  }
);

export const startBankPoller = createAsyncThunk(
  'settings/startBankPoller',
  // eslint-disable-next-line default-param-last
  async ({ intervalMs = 5 * 60 * 1000 } = {}, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post('/admin/watchers/bank-tx-poller/start', {
        intervalMs,
      });

      const payload = res?.data || {};

      await dispatch(bootstrapBankState()).unwrap();

      return {
        watcher: payload?.watcher || null,
        message: payload?.message || 'Bank poller started successfully.',
      };
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to start bank poller'));
    }
  }
);

export const stopBankPoller = createAsyncThunk(
  'settings/stopBankPoller',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstanceNew.post('/admin/watchers/bank-tx-poller/stop');

      const payload = res?.data || {};

      await dispatch(bootstrapBankState()).unwrap();

      return {
        watcher: payload?.watcher || null,
        message: payload?.message || 'Bank poller stopped successfully.',
      };
    } catch (err) {
      return rejectWithValue(getApiErrorMessage(err, 'Failed to stop bank poller'));
    }
  }
);

const initialState = {
  profitPercent: null,
  totalVolume: 0,
  loading: false,
  error: null,
  lastUpdatedAt: null,

  bank: {
    authenticated: false,
    lastAuthAt: null,
    sessionExpiredAt: null,
    sessionValid: null,
    sessionError: null,

    poller: {
      running: false,
      activeFetch: false,
      lastSeenAt: null,
      lastRunAt: null,
      lastSuccessAt: null,
      stopReason: null,
      pausedAt: null,
      stoppedAt: null,
      lastError: null,
      intervalMs: null,
      state: 'disabled',
    },

    balance: null,

    ui: {
      bootstrapping: false,
      loginLoading: false,
      logoutLoading: false,
      fetchBalanceLoading: false,
      startPollerLoading: false,
      stopPollerLoading: false,
      error: null,
      success: null,
    },
  },
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearBankUiState(state) {
      state.bank.ui.error = null;
      state.bank.ui.success = null;
    },
    clearBankBalance(state) {
      state.bank.balance = null;
    },
    setProfitPercent(state, action) {
      state.profitPercent = action.payload.profitPercent;
      state.totalVolume = action.payload.totalVolume;
    },
    setBankPollerStatus(state, action) {
      const { authenticated, running, activeFetch, ts, reason } = action.payload || {};

      if (typeof authenticated === 'boolean') state.bank.authenticated = authenticated;
      if (typeof running === 'boolean') state.bank.poller.running = running;
      if (typeof activeFetch === 'boolean') state.bank.poller.activeFetch = activeFetch;
      if (ts) state.bank.poller.lastSeenAt = ts;
      if (reason) state.bank.poller.stopReason = reason;

      if (reason === 'session_expired' || reason === 'manual_logout') {
        state.bank.authenticated = false;
        state.bank.poller.running = false;
        state.bank.poller.activeFetch = false;
      }
    },
    profitPercentCleared(state) {
      state.profitPercent = null;
      state.error = null;
      state.lastUpdatedAt = null;
    },
    profitPercentSet(state, action) {
      state.profitPercent = action.payload;
      state.error = null;
      state.lastUpdatedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfitPercent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfitPercent.fulfilled, (state, action) => {
        const { profitPercent, totalVolume } = action.payload;
        state.loading = false;
        state.profitPercent = profitPercent;
        state.totalVolume = totalVolume;
        state.lastUpdatedAt = Date.now();
      })
      .addCase(fetchProfitPercent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch profit percent';
      })

      .addCase(bootstrapBankState.pending, (state) => {
        state.bank.ui.bootstrapping = true;
        state.bank.ui.error = null;
      })
      .addCase(bootstrapBankState.fulfilled, (state, action) => {
        const session = action.payload?.session || {};
        const watcher = action.payload?.watcher || {};

        state.bank.ui.bootstrapping = false;

        state.bank.authenticated = Boolean(session.authenticated);
        state.bank.lastAuthAt = session.lastAuthAt ?? null;
        state.bank.sessionExpiredAt = session.sessionExpiredAt ?? null;
        state.bank.sessionValid = session.sessionValid ?? null;
        state.bank.sessionError = session.lastError ?? null;

        state.bank.poller = {
          ...state.bank.poller,
          ...normalizeWatcher(watcher),
        };
      })
      .addCase(bootstrapBankState.rejected, (state, action) => {
        state.bank.ui.bootstrapping = false;
        state.bank.ui.error = action.payload || 'Failed to bootstrap bank state';
      })

      .addCase(loginGolis.pending, (state) => {
        state.bank.ui.loginLoading = true;
        state.bank.ui.error = null;
        state.bank.ui.success = null;
      })
      .addCase(loginGolis.fulfilled, (state, action) => {
        state.bank.ui.loginLoading = false;
        state.bank.ui.success = action.payload?.message || 'Login completed successfully.';
      })
      .addCase(loginGolis.rejected, (state, action) => {
        state.bank.ui.loginLoading = false;
        state.bank.ui.error = action.payload || 'Failed to login';
      })

      .addCase(logoutGolis.pending, (state) => {
        state.bank.ui.logoutLoading = true;
        state.bank.ui.error = null;
        state.bank.ui.success = null;
      })
      .addCase(logoutGolis.fulfilled, (state, action) => {
        state.bank.ui.logoutLoading = false;
        state.bank.balance = null;
        state.bank.ui.success = action.payload?.message || 'Logged out from Golis.';
      })
      .addCase(logoutGolis.rejected, (state, action) => {
        state.bank.ui.logoutLoading = false;
        state.bank.ui.error = action.payload || 'Failed to logout from Golis';
      })

      .addCase(fetchGolisBalanceOnce.pending, (state) => {
        state.bank.ui.fetchBalanceLoading = true;
        state.bank.ui.error = null;
        state.bank.ui.success = null;
      })
      .addCase(fetchGolisBalanceOnce.fulfilled, (state, action) => {
        state.bank.ui.fetchBalanceLoading = false;
        state.bank.balance = action.payload?.balance ?? null;
        state.bank.ui.success = action.payload?.message || 'Balance fetched successfully.';
      })
      .addCase(fetchGolisBalanceOnce.rejected, (state, action) => {
        state.bank.ui.fetchBalanceLoading = false;
        state.bank.ui.error = action.payload || 'Failed to fetch balance';
      })

      .addCase(startBankPoller.pending, (state) => {
        state.bank.ui.startPollerLoading = true;
        state.bank.ui.error = null;
        state.bank.ui.success = null;
      })
      .addCase(startBankPoller.fulfilled, (state, action) => {
        state.bank.ui.startPollerLoading = false;
        if (action.payload?.watcher) {
          state.bank.poller = {
            ...state.bank.poller,
            ...normalizeWatcher(action.payload.watcher),
          };
        }
        state.bank.ui.success = action.payload?.message || 'Bank poller started successfully.';
      })
      .addCase(startBankPoller.rejected, (state, action) => {
        state.bank.ui.startPollerLoading = false;
        state.bank.ui.error = action.payload || 'Failed to start bank poller';
      })

      .addCase(stopBankPoller.pending, (state) => {
        state.bank.ui.stopPollerLoading = true;
        state.bank.ui.error = null;
        state.bank.ui.success = null;
      })
      .addCase(stopBankPoller.fulfilled, (state, action) => {
        state.bank.ui.stopPollerLoading = false;
        if (action.payload?.watcher) {
          state.bank.poller = {
            ...state.bank.poller,
            ...normalizeWatcher(action.payload.watcher),
          };
        }
        state.bank.ui.success = action.payload?.message || 'Bank poller stopped successfully.';
      })
      .addCase(stopBankPoller.rejected, (state, action) => {
        state.bank.ui.stopPollerLoading = false;
        state.bank.ui.error = action.payload || 'Failed to stop bank poller';
      });
  },
});

export const {
  setProfitPercent,
  clearBankUiState,
  clearBankBalance,
  setBankPollerStatus,
  profitPercentCleared,
  profitPercentSet,
} = settingsSlice.actions;

export default settingsSlice.reducer;
